import { createAsyncThunk } from '@reduxjs/toolkit';
import { User } from 'oidc-client';
import LOGIN_FLOW_ACTIONS from './login-action-types';
import { IOrganization } from '../organizations/organizations-claim';
import { setOrganizations } from '../organizations/organizations-slice';
import { updateLoginStatus, authAPIFailed } from './login-slice';
import {
    getUserInfo,
    initiateLogin,
    processLoginCallback,
    authenticateUser,
    getAndCheckUserInfo,
    silentUserLogin,
    initiateLogout,
    initiateChangeOrganization,
} from './login-api';

export interface IAuth0UserInfo extends User {
    isUserAuthenticated: boolean | undefined;
    identityAccessToken: string | undefined;
    identityAccessTokenExpiration: number | undefined;
    firstName: string;
    lastName: string;
    originalRedirectUrl: string;
    organizations: IOrganization[];
    accessToken?: string;
    role: string | undefined;
    email: string | undefined;
}

export const loginFlow = createAsyncThunk(
    LOGIN_FLOW_ACTIONS.LOGIN_FLOW,
    async (_, thunkAPI) => {
        try {
            const oAuthUserInfo = (await getUserInfo()) as IAuth0UserInfo;
            await loginAPI(oAuthUserInfo, thunkAPI);
        } catch (error) {
            console.error('Error retrieving user information:', error);
            await initiateLogin();
        }
    }
);

// Redirects to NeoID's Organization Picker. The existing session is left
// untouched until the callback succeeds, so cancelling changes nothing. A
// failed redirect is only logged: authAPIFailed would mark the still-valid
// session as unauthenticated.
export const changeOrganizationFlow = createAsyncThunk(
    'login/changeOrganizationFlowStatus',
    async () => {
        try {
            await initiateChangeOrganization();
        } catch (error) {
            console.error('Error changing organization:', error);
        }
    }
);

export const silentLoginFlow = createAsyncThunk(
    LOGIN_FLOW_ACTIONS.LOGIN_CHECK_FLOW,
    async (_, thunkAPI) => {
        try {
            const oAuthUserInfo = (await getUserInfo()) as IAuth0UserInfo;
            await loginAPI(oAuthUserInfo, thunkAPI);
        } catch (error) {
            await silentUserLogin();
            const oAuthUserInfo =
                (await getAndCheckUserInfo()) as IAuth0UserInfo;
            await loginAPI(oAuthUserInfo, thunkAPI);
        }
    }
);

export const completeLoginFlow = createAsyncThunk(
    'login/completeLoginFlowStatus',
    async (_, thunkAPI) => {
        try {
            const oAuthUserInfo =
                (await processLoginCallback()) as IAuth0UserInfo;
            await loginAPI(oAuthUserInfo, thunkAPI);
        } catch (error: any) {
            thunkAPI.dispatch(authAPIFailed(error));
        }
    }
);

export const logoutFlow = createAsyncThunk(
    'logout/logoutFlowStatus',
    async (_, thunkAPI) => {
        try {
            await initiateLogout();
        } catch (error) {
            console.error('Error during logout:', error);
            thunkAPI.dispatch(authAPIFailed(error));
        }
    }
);

async function loginAPI(oAuthUserInfo: IAuth0UserInfo, thunkAPI: any) {
    const {
        isUserAuthenticated,
        identityAccessToken,
        identityAccessTokenExpiration,
        firstName,
        lastName,
        originalRedirectUrl,
        organizations,
        accessToken,
    } = oAuthUserInfo;

    try {
        if (identityAccessToken) {
            const { data } = await authenticateUser(
                identityAccessToken,
                identityAccessTokenExpiration as number,
                accessToken
            );

            thunkAPI.dispatch(setOrganizations(organizations || []));
            thunkAPI.dispatch(
                updateLoginStatus({
                    ...data,
                    isUserAuthenticated,
                    originalRedirectUrl,
                })
            );
        } else {
            throw new Error('identityAccessToken is undefined');
        }
    } catch (error: any) {
        throw {
            error: error.response,
            firstName,
            lastName,
            isUserAuthenticated: false,
        };
    }
}
